/**
 * Worker helper utilities for creating and managing Web Workers
 *
 * This module provides centralized logic for creating worker instances
 * in different environments (browser, Node.js, Bun) using import.meta.resolve
 * for server-side and package-relative paths for browsers.
 */

import { isBun } from './env';

export type CreateWorkerOptions = {
  assetPath?: string;
};

/**
 * Create a Web Worker for a specific codec
 *
 * In Node.js/Bun environments, uses import.meta.resolve to locate worker files.
 * In browser environments, uses relative paths within node_modules that Vite can resolve.
 *
 * @param workerFilename - The name of the worker file (e.g., 'resize.worker' or 'webp.worker')
 * @returns Worker instance
 * @throws Error if worker creation fails with detailed error message
 */
export function createCodecWorker(
  workerFilename: string,
  options?: CreateWorkerOptions
): Worker {
  // Ensure filename has correct format
  const normalizedName = workerFilename.endsWith('.js')
    ? workerFilename
    : `${workerFilename}.js`;

  // Map worker filenames to their package and export
  const workerMap: Record<string, { package: string; specifier: string }> = {
    'resize.worker.js': {
      package: '@squoosh-kit/resize',
      specifier: 'resize.worker.js',
    },
    'webp.worker.js': {
      package: '@squoosh-kit/webp',
      specifier: 'webp.worker.js',
    },
    'avif.worker.js': {
      package: '@squoosh-kit/avif',
      specifier: 'avif.worker.js',
    },
    'mozjpeg.worker.js': {
      package: '@squoosh-kit/mozjpeg',
      specifier: 'mozjpeg.worker.js',
    },
    'jxl.worker.js': {
      package: '@squoosh-kit/jxl',
      specifier: 'jxl.worker.js',
    },
    'oxipng.worker.js': {
      package: '@squoosh-kit/oxipng',
      specifier: 'oxipng.worker.js',
    },
    'png.worker.js': {
      package: '@squoosh-kit/png',
      specifier: 'png.worker.js',
    },
    'imagequant.worker.js': {
      package: '@squoosh-kit/imagequant',
      specifier: 'imagequant.worker.js',
    },
    'qoi.worker.js': {
      package: '@squoosh-kit/qoi',
      specifier: 'qoi.worker.js',
    },
    'wp2.worker.js': {
      package: '@squoosh-kit/wp2',
      specifier: 'wp2.worker.js',
    },
    'hqx.worker.js': {
      package: '@squoosh-kit/hqx',
      specifier: 'hqx.worker.js',
    },
    'rotate.worker.js': {
      package: '@squoosh-kit/rotate',
      specifier: 'rotate.worker.js',
    },
    'visdif.worker.js': {
      package: '@squoosh-kit/visdif',
      specifier: 'visdif.worker.js',
    },
  };

  const workerConfig = workerMap[normalizedName];
  if (!workerConfig) {
    throw new Error(
      `Unknown worker: ${normalizedName}. ` +
        `Supported workers: ${Object.keys(workerMap).join(', ')}`
    );
  }

  try {
    // In browser contexts, use relative paths within the installed packages
    if (typeof window !== 'undefined') {
      const packageName = workerConfig.package.split('/')[1]; // Extract 'resize' or 'webp'
      const workerFile = normalizedName.replace('.js', '.browser.mjs');

      console.log(
        `[worker-helper] In browser environment. Trying to create worker:`
      );
      console.log(`[worker-helper]   - Package Name: ${packageName}`);
      console.log(`[worker-helper]   - Worker File: ${workerFile}`);

      // If a custom asset path is provided, use it directly
      if (options?.assetPath) {
        // Normalize the asset path - ensure it starts with / and ends without /
        let normalizedAssetPath = options.assetPath;
        if (!normalizedAssetPath.startsWith('/')) {
          normalizedAssetPath = '/' + normalizedAssetPath;
        }
        if (normalizedAssetPath.endsWith('/')) {
          normalizedAssetPath = normalizedAssetPath.slice(0, -1);
        }

        // Construct absolute URL: {origin}{assetPath}/{package}/{workerFile}
        const workerPath = `${normalizedAssetPath}/${packageName}/${workerFile}`;
        const workerUrl = new URL(workerPath, window.location.origin).href;

        console.log(
          `[worker-helper] Using provided assetPath. Full Worker URL: ${workerUrl}`
        );
        try {
          const worker = new Worker(workerUrl, { type: 'module' });
          console.log(
            `[worker-helper] Successfully created worker with assetPath: ${workerUrl}`
          );
          return worker;
        } catch (e) {
          console.error(
            `[worker-helper] Failed to load worker from assetPath URL: ${workerUrl}`,
            e
          );
          throw new Error(
            `Worker failed to load from ${workerUrl}: ${e instanceof Error ? e.message : String(e)}`,
            { cause: e }
          );
        }
      }

      // Try multiple path strategies to support both:
      // 1. Monorepo development structure: ../../{package}/dist/{workerFile}
      // 2. npm installed structure: ../../../{package}/dist/{workerFile}
      const pathStrategies = [
        // First try monorepo structure (when runtime is at packages/runtime/src)
        `../../${packageName}/dist/${workerFile}`,
        // Then try npm structure (when runtime is at node_modules/@squoosh-kit/runtime)
        `../../../node_modules/@squoosh-kit/${packageName}/dist/${workerFile}`,
        // Alternative npm structure for cases where packages are flattened
        `../../../${packageName}/dist/${workerFile}`,
      ];

      let lastError: Error | null = null;

      for (const relPath of pathStrategies) {
        console.log('relPath:', relPath);
        console.log('import.meta.url:', import.meta.url);
        try {
          const workerUrl = new URL(relPath, import.meta.url);
          console.log(
            `[worker-helper] Trying path strategy. Full Worker URL: ${workerUrl.href}`
          );
          const worker = new Worker(workerUrl, {
            type: 'module',
          });
          console.log(
            `[worker-helper] Successfully created worker with URL: ${workerUrl.href}`
          );
          return worker;
        } catch (error) {
          console.warn(
            `[worker-helper] Path strategy failed for ${relPath}:`,
            error
          );
          lastError = error instanceof Error ? error : new Error(String(error));
          // Continue to next strategy
        }
      }

      // If all strategies failed, throw the last error
      if (lastError) {
        console.error('[worker-helper] All path strategies failed.', lastError);
        throw lastError;
      }
      throw new Error(
        `Could not resolve worker ${normalizedName} using any available path strategy`
      );
    }

    // Fallbacks for monorepo/dev without build artifacts
    const platformExt = isBun() ? '.bun.js' : '.node.mjs';
    const baseName = normalizedName.replace('.js', '');
    const pkgName = workerConfig.package.split('/')[1]; // e.g. 'avif', 'webp', 'resize'

    // 1) Try TypeScript source first (Bun can transpile TS, works in dev)
    const srcRelPath = `../../${pkgName}/src/${baseName}.ts`;

    console.log('srcRelPath:', srcRelPath);
    console.log('import.meta.url:', import.meta.url);
    try {
      return new Worker(new URL(srcRelPath, import.meta.url), {
        type: 'module',
      });
    } catch {
      // 2) Try dist output (if already built)
      const distRelPath = `../../${pkgName}/dist/${baseName}.${platformExt.slice(1)}`;

      console.log('distRelPath:', distRelPath);
      console.log('import.meta.url:', import.meta.url);
      try {
        return new Worker(new URL(distRelPath, import.meta.url), {
          type: 'module',
        });
      } catch {
        // 3) Try import.meta.resolve as last resort
        if (typeof import.meta.resolve === 'function') {
          try {
            const resolved = import.meta.resolve(
              `${workerConfig.package}/${workerConfig.specifier}`
            );
            console.log('resolved:', resolved);
            return new Worker(resolved, { type: 'module' });
          } catch {
            // Continue to error below
          }
        }
      }
    }

    // If we get here, all fallbacks failed
    throw new Error(
      `Failed to create worker from ${normalizedName}. ` +
        `Tried TypeScript source, dist output, and import.meta.resolve. ` +
        `Ensure the @squoosh-kit/resize and @squoosh-kit/webp packages are installed.`
    );
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(
      `Failed to create worker from ${normalizedName}: ${errorMessage}. ` +
        `Ensure the @squoosh-kit/resize and @squoosh-kit/webp packages are installed. ` +
        `If you're using Vite, ensure the worker files are not being optimized as dependencies.`,
      { cause: error }
    );
  }
}

/**
 * Create a worker with initialization timeout and ready signal handling
 *
 * This is a higher-level function that creates a worker and waits for it
 * to signal that it's ready to receive messages.
 *
 * @param workerFilename - The name of the worker file
 * @param timeoutMs - Timeout in milliseconds (default: 10000)
 * @returns Promise that resolves to a ready Worker instance
 * @throws Error if worker creation fails or times out
 */
export function createReadyWorker(
  workerFilename: string,
  options?: CreateWorkerOptions,
  timeoutMs: number = 10000
): Promise<Worker> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(
        new Error(
          `Worker initialization timeout after ${timeoutMs}ms. Worker file: ${workerFilename}`
        )
      );
    }, timeoutMs);

    let worker: Worker;
    try {
      worker = createCodecWorker(workerFilename, options);
    } catch (error) {
      clearTimeout(timeout);
      reject(error);
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'worker:ready') {
        clearTimeout(timeout);
        worker.removeEventListener('message', handleMessage);
        worker.removeEventListener('error', handleError);
        worker.removeEventListener('messageerror', handleMessageError);
        resolve(worker);
      }
    };

    const handleError = (event: ErrorEvent) => {
      clearTimeout(timeout);
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      worker.removeEventListener('messageerror', handleMessageError);
      reject(
        new Error(
          `Worker failed to start: ${event?.message || 'Unknown error'}. Worker file: ${workerFilename}`
        )
      );
    };

    const handleMessageError = () => {
      clearTimeout(timeout);
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      worker.removeEventListener('messageerror', handleMessageError);
      reject(
        new Error(
          `Worker message error during initialization. Worker file: ${workerFilename}`
        )
      );
    };

    worker.addEventListener('message', handleMessage);
    worker.addEventListener('error', handleError);
    worker.addEventListener('messageerror', handleMessageError);
    worker.postMessage({ type: 'worker:ping' });
  });
}
